/**
 * FERP Portal - Primitive Detail Panel
 *
 * Side panel for viewing, creating, and editing primitives.
 * Opens when clicking a primitive in the 5×5 grid.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only (NO MUI)
 * - Backend ID generation via useGenerateId
 * - Repository pattern via generated mutations
 * - Multi-tenant isolation via useTenantId()
 *
 * DRR-062: Annotations are integrated here so ALL 25 primitives
 * automatically get annotation support via the single code path.
 */

import { useState, useMemo } from 'react';
import {
  Drawer,
  Stack,
  Group,
  Title,
  Text,
  Badge,
  Button,
  Table,
  ScrollArea,
  ActionIcon,
  ThemeIcon,
  Tooltip,
  Modal,
  Alert,
  Loader,
  Center,
  TextInput,
  Pagination,
  Menu,
  Divider,
  Collapse,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconPlus,
  IconEdit,
  IconArchive,
  IconEye,
  IconAlertTriangle,
  IconSearch,
  IconDotsVertical,
  IconRefresh,
  IconX,
  IconCheck,
  IconMessage,
  IconChevronUp,
  IconChevronDown,
} from '@tabler/icons-react';
import { useAnnotations } from '@/hooks/useAnnotations';
import { AnnotationPanel } from '@/components/AnnotationPanel';
import {
  type PrimitiveType,
  type AnyPrimitive,
  type PrimitiveData,
  PRIMITIVE_PHASES,
} from './hooks/usePrimitiveData';
import { FormSafetyLevel } from '@/generated/graphql';
import { getLifeCriticalBorderStyle, SAFETY_LEVEL_UI_CONFIG } from '@/config/safety-levels';

// ============================================================================
// TYPES
// ============================================================================

interface PrimitiveDetailPanelProps {
  primitiveType: PrimitiveType | null;
  primitiveData: PrimitiveData<AnyPrimitive> | null;
  opened: boolean;
  onClose: () => void;
  onView?: (item: AnyPrimitive) => void;
  onEdit?: (item: AnyPrimitive) => void;
  onCreate?: (type: PrimitiveType) => void;
  /** Archive handler - FERP is append-only, nothing is truly deleted */
  onArchive?: (item: AnyPrimitive) => void;
}

interface PrimitiveRow {
  id: string;
  name: string;
  status?: string;
  safetyLevel?: FormSafetyLevel;
  createdAt: string;
  raw: AnyPrimitive;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const PRIMITIVE_LABELS: Record<PrimitiveType, { singular: string; plural: string; description: string }> = {
  // DESIGN
  workflow: { singular: 'Workflow', plural: 'Workflows', description: 'Process templates and definitions' },
  template: { singular: 'Template', plural: 'Templates', description: 'Reusable document and process templates' },
  dependency: { singular: 'Dependency', plural: 'Dependencies', description: 'Workflow ordering and prerequisites' },
  policy: { singular: 'Policy', plural: 'Policies', description: 'Zen Engine policy rules' },
  role: { singular: 'Role', plural: 'Roles', description: 'Access roles and RBAC definitions' },
  // CONFIGURE
  team: { singular: 'Team', plural: 'Teams', description: 'Team groups and organizational units' },
  actor: { singular: 'Actor', plural: 'Actors', description: 'User and system identities' },
  validation: { singular: 'Validation', plural: 'Validations', description: 'Validation rules and checks' },
  contract: { singular: 'Contract', plural: 'Contracts', description: 'SLA definitions and agreements' },
  constraint: { singular: 'Constraint', plural: 'Constraints', description: 'Validation rules and boundaries' },
  // EXECUTE
  instance: { singular: 'Instance', plural: 'Instances', description: 'Running workflow instances' },
  task: { singular: 'Task', plural: 'Tasks', description: 'Work items and assignments' },
  approval: { singular: 'Approval', plural: 'Approvals', description: 'Approval gates and sign-offs' },
  document: { singular: 'Document', plural: 'Documents', description: 'Document artifacts and files' },
  queue: { singular: 'Queue', plural: 'Queues', description: 'Task routing and assignment' },
  // MONITOR
  event: { singular: 'Event', plural: 'Events', description: 'Triggers and notifications' },
  notification: { singular: 'Notification', plural: 'Notifications', description: 'User notifications' },
  trace: { singular: 'Trace', plural: 'Traces', description: 'Execution traces and spans' },
  checkpoint: { singular: 'Checkpoint', plural: 'Checkpoints', description: 'Progress markers and milestones' },
  transaction: { singular: 'Transaction', plural: 'Transactions', description: 'Financial transaction records' },
  // OPTIMIZE
  audit: { singular: 'Audit', plural: 'Audits', description: 'Immutable audit trail' },
  analytics: { singular: 'Analytics', plural: 'Analytics', description: 'Data analysis and reporting' },
  schedule: { singular: 'Schedule', plural: 'Schedules', description: 'Time-based triggers' },
  resource: { singular: 'Resource', plural: 'Resources', description: 'System resources and capacity' },
  integration: { singular: 'Integration', plural: 'Integrations', description: 'External system connections' },
};

const PHASE_COLORS: Record<string, string> = {
  DESIGN: 'blue',
  CONFIGURE: 'grape',
  EXECUTE: 'cyan',
  MONITOR: 'orange',
  OPTIMIZE: 'pink',
};

const ITEMS_PER_PAGE = 10;

// ============================================================================
// HELPERS
// ============================================================================

function extractPrimitiveRow(item: AnyPrimitive, _type: PrimitiveType): PrimitiveRow {
  // Safely extract id - not all fragment types have id field
  const itemId = 'id' in item ? (item.id as string) :
                 'schemaId' in item ? (item.schemaId as string) :
                 'eventId' in item ? (item.eventId as string) :
                 crypto.randomUUID();

  const createdAtValue = 'createdAt' in item && item.createdAt ?
    String(item.createdAt) : new Date().toISOString();

  const base = {
    id: itemId,
    createdAt: createdAtValue,
    raw: item,
  };

  // Extract name based on primitive type
  let name = itemId;
  let status: string | undefined;
  let safetyLevel: FormSafetyLevel | undefined;

  if ('name' in item && item.name) {
    name = item.name as string;
  } else if ('title' in item && item.title) {
    name = item.title as string;
  } else if ('action' in item && item.action) {
    name = item.action as string;
  } else if ('type' in item && item.type) {
    name = `${item.type}`;
  }

  if ('status' in item) {
    status = item.status as string;
  }

  if ('safetyLevel' in item) {
    safetyLevel = item.safetyLevel as FormSafetyLevel;
  }

  return { ...base, name, status, safetyLevel };
}

function getStatusColor(status?: string): string {
  if (!status) return 'gray';
  const s = status.toUpperCase();
  if (s.includes('ACTIVE') || s.includes('COMPLETED') || s.includes('SUCCESS') || s.includes('COMPLIANT')) return 'green';
  if (s.includes('PENDING') || s.includes('DRAFT') || s.includes('PARTIAL')) return 'yellow';
  if (s.includes('FAILED') || s.includes('ERROR') || s.includes('CRITICAL') || s.includes('NON_COMPLIANT')) return 'red';
  if (s.includes('RUNNING') || s.includes('IN_PROGRESS') || s.includes('GENERATING')) return 'blue';
  return 'gray';
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PrimitiveDetailPanel({
  primitiveType,
  primitiveData,
  opened,
  onClose,
  onView,
  onEdit,
  onCreate,
  onArchive,
}: PrimitiveDetailPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [archiveModalOpened, { open: openArchiveModal, close: closeArchiveModal }] = useDisclosure(false);
  const [itemToArchive, setItemToArchive] = useState<AnyPrimitive | null>(null);

  // DRR-062: Selected item for annotation panel (single code path)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [annotationsOpen, { toggle: toggleAnnotations }] = useDisclosure(false);

  // Annotations hook — only active when an item is selected
  const annotationState = useAnnotations(
    primitiveType ?? '',
    selectedItemId ?? '',
    { skip: !selectedItemId || !primitiveType },
  );

  // Get labels and phase
  const labels = primitiveType ? PRIMITIVE_LABELS[primitiveType] : null;
  const phase = primitiveType ? PRIMITIVE_PHASES[primitiveType] : null;
  const phaseColor = phase ? PHASE_COLORS[phase] : 'gray';

  // Transform items to rows
  const rows = useMemo(() => {
    if (!primitiveData || !primitiveType) return [];
    return primitiveData.items.map(item => extractPrimitiveRow(item, primitiveType));
  }, [primitiveData, primitiveType]);

  // Filter rows by search
  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter(row =>
      row.name.toLowerCase().includes(q) ||
      row.id.toLowerCase().includes(q) ||
      (row.status?.toLowerCase().includes(q))
    );
  }, [rows, searchQuery]);

  // Paginate
  const totalPages = Math.ceil(filteredRows.length / ITEMS_PER_PAGE);
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredRows.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredRows, page]);

  // Reset page when search changes
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  // Handle archive confirmation (FERP is append-only - "delete" means archive)
  const handleArchiveClick = (item: AnyPrimitive) => {
    setItemToArchive(item);
    openArchiveModal();
  };

  const handleConfirmArchive = () => {
    if (itemToArchive && onArchive) {
      onArchive(itemToArchive);
    }
    setItemToArchive(null);
    closeArchiveModal();
  };

  // Check if primitive is life-critical based on type
  const isLifeCriticalType = primitiveType && [
    'workflow', 'policy', 'actor', 'task', 'approval',
    'audit', 'contract',
  ].includes(primitiveType);

  if (!primitiveType || !labels) {
    return null;
  }

  return (
    <>
      <Drawer
        opened={opened}
        onClose={() => {
          setSelectedItemId(null);
          onClose();
        }}
        title={null}
        position="right"
        size="lg"
        padding="md"
        styles={{
          header: { display: 'none' },
          body: { padding: 0, height: '100%' },
        }}
      >
        <Stack h="100%" gap={0}>
          {/* Header */}
          <Group
            justify="space-between"
            p="md"
            style={{
              borderBottom: '1px solid var(--mantine-color-gray-3)',
              ...(isLifeCriticalType ? { borderLeft: '4px solid var(--mantine-color-red-6)' } : {}),
            }}
          >
            <div>
              <Group gap="xs" mb={4}>
                <Badge color={phaseColor} variant="light" size="sm">
                  {phase}
                </Badge>
                {isLifeCriticalType && (
                  <Badge color="red" variant="light" size="sm" leftSection={<IconAlertTriangle size={10} />}>
                    Life-Critical
                  </Badge>
                )}
              </Group>
              <Title order={3}>{labels.plural}</Title>
              <Text size="sm" c="dimmed">{labels.description}</Text>
            </div>
            <Group gap="xs">
              <Tooltip label="Refresh">
                <ActionIcon
                  variant="light"
                  onClick={() => primitiveData?.refetch()}
                  loading={primitiveData?.loading}
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
              <ActionIcon variant="light" onClick={onClose}>
                <IconX size={16} />
              </ActionIcon>
            </Group>
          </Group>

          {/* Stats Bar */}
          <Group p="md" gap="lg" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <div>
              <Text size="xl" fw={700}>{primitiveData?.stats.total ?? 0}</Text>
              <Text size="xs" c="dimmed">Total</Text>
            </div>
            <div>
              <Text size="xl" fw={700} c="blue">{primitiveData?.stats.active ?? 0}</Text>
              <Text size="xs" c="dimmed">Active</Text>
            </div>
            {primitiveData?.stats.lifeCritical ? (
              <div>
                <Text size="xl" fw={700} c="red">{primitiveData.stats.lifeCritical}</Text>
                <Text size="xs" c="dimmed">Life-Critical</Text>
              </div>
            ) : null}
            <div style={{ marginLeft: 'auto' }}>
              <ThemeIcon
                size="lg"
                radius="xl"
                color={primitiveData?.stats.health === 'healthy' ? 'green' :
                       primitiveData?.stats.health === 'degraded' ? 'yellow' : 'red'}
                variant="light"
              >
                {primitiveData?.stats.health === 'healthy' ? <IconCheck size={16} /> :
                 primitiveData?.stats.health === 'degraded' ? <IconAlertTriangle size={16} /> :
                 <IconX size={16} />}
              </ThemeIcon>
            </div>
          </Group>

          {/* Toolbar */}
          <Group p="md" gap="sm">
            <TextInput
              placeholder={`Search ${labels.plural.toLowerCase()}...`}
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ flex: 1 }}
            />
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => onCreate?.(primitiveType)}
            >
              Create {labels.singular}
            </Button>
          </Group>

          {/* Table */}
          <ScrollArea style={{ flex: 1 }} p="md" pt={0}>
            {primitiveData?.loading ? (
              <Center py="xl">
                <Loader size="lg" />
              </Center>
            ) : paginatedRows.length === 0 ? (
              <Alert color="gray" variant="light" title={searchQuery ? 'No results' : 'No data'}>
                {searchQuery
                  ? `No ${labels.plural.toLowerCase()} matching "${searchQuery}"`
                  : `No ${labels.plural.toLowerCase()} found. Create one to get started.`}
              </Alert>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Safety</Table.Th>
                    <Table.Th>Created</Table.Th>
                    <Table.Th w={80}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginatedRows.map((row) => (
                    <Table.Tr
                      key={row.id}
                      onClick={() => setSelectedItemId(row.id)}
                      style={{
                        cursor: 'pointer',
                        ...(row.id === selectedItemId
                          ? { backgroundColor: 'var(--mantine-color-blue-0)' }
                          : {}),
                        ...(row.safetyLevel === FormSafetyLevel.LIFE_CRITICAL
                          ? getLifeCriticalBorderStyle(FormSafetyLevel.LIFE_CRITICAL)
                          : {}),
                      }}
                    >
                      <Table.Td>
                        <Stack gap={0}>
                          <Text size="sm" fw={500} lineClamp={1}>{row.name}</Text>
                          <Text size="xs" c="dimmed" ff="monospace">{row.id}</Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        {row.status ? (
                          <Badge color={getStatusColor(row.status)} variant="light" size="sm">
                            {row.status.replace(/_/g, ' ')}
                          </Badge>
                        ) : (
                          <Text size="xs" c="dimmed">-</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {row.safetyLevel ? (
                          <Badge
                            color={SAFETY_LEVEL_UI_CONFIG[row.safetyLevel]?.color ?? 'gray'}
                            variant="light"
                            size="sm"
                          >
                            {row.safetyLevel.replace(/_/g, ' ')}
                          </Badge>
                        ) : (
                          <Text size="xs" c="dimmed">-</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {new Date(row.createdAt).toLocaleDateString()}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Menu shadow="md" position="bottom-end">
                          <Menu.Target>
                            <ActionIcon variant="subtle" size="sm">
                              <IconDotsVertical size={14} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconEye size={14} />}
                              onClick={() => onView?.(row.raw)}
                            >
                              View Details
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconEdit size={14} />}
                              onClick={() => onEdit?.(row.raw)}
                            >
                              Edit
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item
                              leftSection={<IconArchive size={14} />}
                              color="orange"
                              onClick={() => handleArchiveClick(row.raw)}
                            >
                              Archive
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </ScrollArea>

          {/* Pagination */}
          {totalPages > 1 && (
            <Group justify="center" p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
              <Pagination
                value={page}
                onChange={setPage}
                total={totalPages}
                size="sm"
              />
            </Group>
          )}

          {/* DRR-062: Annotation Panel — single code path for all 25 primitives */}
          {selectedItemId && (
            <>
              <Divider />
              <Group
                p="xs"
                px="md"
                justify="space-between"
                style={{ cursor: 'pointer', borderTop: '1px solid var(--mantine-color-gray-3)' }}
                onClick={toggleAnnotations}
              >
                <Group gap="xs">
                  <IconMessage size={16} />
                  <Text size="sm" fw={600}>
                    Annotations
                  </Text>
                  {annotationState.total > 0 && (
                    <Badge size="xs" variant="light" color="blue">
                      {annotationState.total}
                    </Badge>
                  )}
                </Group>
                {annotationsOpen ? <IconChevronDown size={14} /> : <IconChevronUp size={14} />}
              </Group>
              <Collapse in={annotationsOpen}>
                <ScrollArea mah={300} p="md" pt={0}>
                  <AnnotationPanel {...annotationState} />
                </ScrollArea>
              </Collapse>
            </>
          )}
        </Stack>
      </Drawer>

      {/* Archive Confirmation Modal - FERP is append-only */}
      <Modal
        opened={archiveModalOpened}
        onClose={closeArchiveModal}
        title={`Archive ${labels.singular}?`}
        centered
      >
        <Stack>
          <Alert color="orange" variant="light" icon={<IconArchive size={16} />}>
            This will archive the record. It will be hidden from active views but preserved
            in the audit trail for compliance and historical reference. FERP is append-only.
          </Alert>
          {itemToArchive && 'name' in itemToArchive && (
            <Text>
              Are you sure you want to archive <strong>{(itemToArchive as { name: string }).name}</strong>?
            </Text>
          )}
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeArchiveModal}>
              Cancel
            </Button>
            <Button color="orange" onClick={handleConfirmArchive}>
              Archive
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default PrimitiveDetailPanel;
